import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, Scissors, Sparkles, Download, X, RotateCcw, Video, Eye } from "lucide-react";

/**
 * MVP photo editing tab.
 * Two AI actions: background removal + auto enhance (both via fal.ai).
 * Uploads → returns edited photo URL(s) → user can download or reuse in Video tab.
 */
const OPERATIONS = [
  {
    id: "bg_remove",
    label: "Remove background",
    icon: Scissors,
    blurb: "Isoleert het onderwerp — clean cutout klaar voor collages of overlays",
    credits: 3,
  },
  {
    id: "enhance",
    label: "AI Enhance",
    icon: Sparkles,
    blurb: "Verhoogt scherpte, licht & kleur automatisch",
    credits: 5,
  },
];

export default function StudioPhotoEditor() {
  const nav = useNavigate();
  const { isOwner, balance, refreshBalance } = useAuth();
  const [op, setOp] = useState("bg_remove");
  const [items, setItems] = useState([]); // [{ id, previewUrl, status, resultUrl, error }]
  const [busy, setBusy] = useState(false);
  const [previewItem, setPreviewItem] = useState(null); // item shown in modal
  const [sendingToVideo, setSendingToVideo] = useState(false);
  const fileRef = useRef(null);

  // Only unprocessed items count toward the next-run credit total, so users
  // can add new photos without re-paying for already-completed ones.
  const pendingItems = items.filter((it) => it.status !== "done" && it.status !== "processing");
  const currentCost = OPERATIONS.find((o) => o.id === op)?.credits || 0;
  const totalCredits = pendingItems.length * currentCost;
  const canAfford = isOwner || balance?.unlimited || (balance?.balance ?? 0) >= totalCredits;

  const doneItems = items.filter((it) => it.status === "done" && it.resultUrl);

  const onPick = (files) => {
    const added = Array.from(files || []).slice(0, 30 - items.length).map((f) => ({
      id: Math.random().toString(36).slice(2, 10),
      file: f,
      previewUrl: URL.createObjectURL(f),
      status: "idle",
    }));
    if (added.length === 0) return;
    setItems((it) => [...it, ...added]);
  };

  const removeItem = (id) => {
    setItems((it) => {
      const found = it.find((x) => x.id === id);
      if (found?.previewUrl) URL.revokeObjectURL(found.previewUrl);
      return it.filter((x) => x.id !== id);
    });
  };

  const run = async () => {
    if (!items.length) { toast.info("Voeg eerst foto's toe"); return; }
    if (!pendingItems.length) { toast.info("Alle foto's zijn al bewerkt"); return; }
    if (!canAfford) { toast.error(`${totalCredits} credits nodig, saldo ${balance?.balance ?? 0}`); return; }
    setBusy(true);
    for (const it of pendingItems) {
      setItems((all) => all.map((x) => x.id === it.id ? { ...x, status: "processing", error: null } : x));
      try {
        const fd = new FormData();
        fd.append("file", it.file);
        fd.append("operation", op);
        const { data } = await api.post("/photos/edit", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems((all) => all.map((x) => x.id === it.id ? { ...x, status: "done", resultUrl: data.url, opUsed: op } : x));
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Bewerking mislukt";
        setItems((all) => all.map((x) => x.id === it.id ? { ...x, status: "failed", error: msg } : x));
      }
    }
    await refreshBalance();
    setBusy(false);
  };

  const retryItem = async (id) => {
    const it = items.find((x) => x.id === id);
    if (!it || busy) return;
    if (!canAfford && !isOwner) {
      toast.error("Onvoldoende credits voor retry");
      return;
    }
    setBusy(true);
    setItems((all) => all.map((x) => x.id === id ? { ...x, status: "processing", error: null } : x));
    try {
      const fd = new FormData();
      fd.append("file", it.file);
      fd.append("operation", op);
      const { data } = await api.post("/photos/edit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((all) => all.map((x) => x.id === id ? { ...x, status: "done", resultUrl: data.url, opUsed: op } : x));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Bewerking mislukt";
      setItems((all) => all.map((x) => x.id === id ? { ...x, status: "failed", error: msg } : x));
    }
    await refreshBalance();
    setBusy(false);
  };

  const sendToVideo = async (which /* "all" | id */) => {
    const urls = which === "all"
      ? doneItems.map((x) => x.resultUrl)
      : [items.find((x) => x.id === which)?.resultUrl].filter(Boolean);
    if (!urls.length) { toast.info("Geen bewerkte foto's"); return; }
    setSendingToVideo(true);
    try {
      const { data } = await api.post("/agent/stage-from-edits", { edit_urls: urls });
      toast.success(`${data.count} foto's klaar in de Video-tab`);
      nav(`/?tab=video&session=${data.session_id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Verzenden mislukt";
      toast.error(msg);
    } finally {
      setSendingToVideo(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto w-full px-4" data-testid="photo-editor">
      <div className="text-center mb-6">
        <p className="text-sm text-zinc-400">
          Upload foto's, kies een bewerking, en gebruik het resultaat straks weer in de Video-tab.
        </p>
      </div>

      {/* Operation picker */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {OPERATIONS.map((o) => {
          const Icon = o.icon;
          const active = op === o.id;
          return (
            <button
              key={o.id}
              onClick={() => setOp(o.id)}
              data-testid={`photo-op-${o.id}`}
              className={`text-left rounded-xl border transition-colors p-4 ${
                active ? "border-amber-500 bg-amber-500/[0.06]" : "border-white/5 bg-[#121214] hover:border-white/15"
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <Icon className={`w-5 h-5 ${active ? "text-amber-400" : "text-zinc-400"}`} strokeWidth={1.75} />
                <span className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-wider">
                  {o.credits} cr / photo
                </span>
              </div>
              <div className="text-sm font-medium">{o.label}</div>
              <div className="text-xs text-zinc-400 mt-1">{o.blurb}</div>
            </button>
          );
        })}
      </div>

      {/* Upload zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files); }}
        data-testid="photo-dropzone"
        className="cursor-pointer rounded-xl border-2 border-dashed border-white/10 bg-white/[0.02] hover:bg-white/[0.04] p-10 text-center transition"
      >
        <input
          ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp"
          onChange={(e) => { onPick(e.target.files); e.target.value = ""; }}
          className="hidden" data-testid="photo-file-input"
        />
        <Upload className="w-8 h-8 text-amber-400 mx-auto mb-3" strokeWidth={1.5} />
        <div className="font-heading text-lg font-bold tracking-tight">Sleep foto's hierheen of klik om te bladeren</div>
        <div className="text-zinc-500 text-xs mt-2 font-mono-tech uppercase tracking-wider">
          JPG · PNG · WebP · tot 30 foto's
        </div>
      </div>

      {/* Item grid */}
      {items.length > 0 && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="font-mono-tech text-[10px] text-zinc-500">
              {items.length} · PHOTOS · {pendingItems.length > 0
                ? <>{totalCredits} CREDITS VOOR VOLGENDE RUN</>
                : <>{doneItems.length} KLAAR</>}
            </div>
            <div className="flex gap-2">
              {doneItems.length > 1 && (
                <Button
                  onClick={() => sendToVideo("all")}
                  disabled={sendingToVideo}
                  data-testid="photo-send-all-to-video"
                  variant="outline"
                  className="border-white/10 hover:bg-white/5"
                >
                  {sendingToVideo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Video className="w-4 h-4 mr-2" />}
                  Alles naar Video ({doneItems.length})
                </Button>
              )}
              <Button
                onClick={run}
                disabled={busy || !canAfford || pendingItems.length === 0}
                data-testid="photo-run"
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Run {OPERATIONS.find((o) => o.id === op)?.label}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`photo-item-${it.id}`}
                className="relative bg-[#121214] border border-white/5 rounded-lg overflow-hidden group"
              >
                <button
                  onClick={() => it.status === "done" && setPreviewItem(it)}
                  disabled={it.status !== "done"}
                  className="w-full aspect-square relative block disabled:cursor-default"
                  data-testid={`photo-preview-open-${it.id}`}
                >
                  <img
                    src={it.resultUrl ? fileUrl(it.resultUrl) : it.previewUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{
                      backgroundImage: op === "bg_remove"
                        ? "linear-gradient(45deg,#333 25%,transparent 25%),linear-gradient(-45deg,#333 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#333 75%),linear-gradient(-45deg,transparent 75%,#333 75%)"
                        : undefined,
                      backgroundSize: "16px 16px",
                      backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
                    }}
                  />
                  {it.status === "processing" && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                    </div>
                  )}
                  {it.status === "failed" && (
                    <div className="absolute inset-0 bg-red-900/70 flex flex-col items-center justify-center p-2 text-center text-[11px] text-white gap-2">
                      <span>{it.error?.slice(0, 60) || "Failed"}</span>
                    </div>
                  )}
                  {it.status === "done" && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition">
                      <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition" />
                    </div>
                  )}
                </button>
                <button
                  onClick={() => removeItem(it.id)}
                  data-testid={`photo-remove-${it.id}`}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 hover:bg-red-500 transition z-10"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
                {it.status === "failed" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); retryItem(it.id); }}
                    disabled={busy}
                    data-testid={`photo-retry-${it.id}`}
                    className="absolute top-2 left-2 h-7 px-2 rounded-full bg-amber-500 text-black text-[10px] font-semibold flex items-center gap-1 hover:bg-amber-400 disabled:opacity-50 z-10"
                  >
                    <RotateCcw className="w-3 h-3" strokeWidth={2.5} />
                    Retry
                  </button>
                )}
                {it.status === "done" && (
                  <div className="p-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono-tech uppercase">
                    <span className="text-emerald-400">Klaar</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => sendToVideo(it.id)}
                        title="Gebruik in Video"
                        data-testid={`photo-send-video-${it.id}`}
                        className="text-zinc-400 hover:text-white transition"
                      >
                        <Video className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                      <a
                        href={fileUrl(it.resultUrl)}
                        download
                        data-testid={`photo-download-${it.id}`}
                        className="text-amber-400 hover:text-amber-300 flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" strokeWidth={2} />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview modal — before/after */}
      {previewItem && (
        <div
          onClick={() => setPreviewItem(null)}
          data-testid="photo-preview-modal"
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4 sm:p-8"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#0e0e10] border border-white/10 rounded-2xl overflow-hidden w-full max-w-5xl"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <div className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-400">
                Voor · Na
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => { sendToVideo(previewItem.id); setPreviewItem(null); }}
                  disabled={sendingToVideo}
                  data-testid="photo-preview-send-video"
                  variant="outline"
                  size="sm"
                  className="border-white/10 hover:bg-white/5"
                >
                  <Video className="w-3.5 h-3.5 mr-1.5" /> Gebruik in Video
                </Button>
                <a
                  href={fileUrl(previewItem.resultUrl)}
                  download
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
                <button
                  onClick={() => setPreviewItem(null)}
                  className="w-8 h-8 rounded-md hover:bg-white/5 flex items-center justify-center"
                  data-testid="photo-preview-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <BeforeAfter beforeUrl={previewItem.previewUrl} afterUrl={fileUrl(previewItem.resultUrl)} showChecker={previewItem.opUsed === "bg_remove"} />
          </div>
        </div>
      )}
    </div>
  );
}


/** Before/after slider — pure CSS clip-path driven by a range input. */
function BeforeAfter({ beforeUrl, afterUrl, showChecker = false }) {
  const [pct, setPct] = useState(50);
  return (
    <div className="relative w-full bg-black" style={{ aspectRatio: "16/10" }}>
      <img src={beforeUrl} alt="voor" className="absolute inset-0 w-full h-full object-contain" />
      <div
        className="absolute inset-0"
        style={{
          clipPath: `inset(0 ${100 - pct}% 0 0)`,
          backgroundImage: showChecker
            ? "linear-gradient(45deg,#333 25%,transparent 25%),linear-gradient(-45deg,#333 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#333 75%),linear-gradient(-45deg,transparent 75%,#333 75%)"
            : undefined,
          backgroundSize: "20px 20px",
          backgroundPosition: "0 0,0 10px,10px -10px,-10px 0",
        }}
      >
        <img src={afterUrl} alt="na" className="w-full h-full object-contain" />
      </div>
      {/* Divider handle */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-amber-400 pointer-events-none"
        style={{ left: `${pct}%` }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-amber-400 border-2 border-black flex items-center justify-center text-black text-xs font-bold">
          ⇔
        </div>
      </div>
      {/* Labels */}
      <div className="absolute top-3 left-3 font-mono-tech text-[10px] uppercase tracking-wider text-white/70 bg-black/50 backdrop-blur px-2 py-1 rounded">
        Voor
      </div>
      <div className="absolute top-3 right-3 font-mono-tech text-[10px] uppercase tracking-wider text-white bg-amber-500/90 px-2 py-1 rounded">
        Na
      </div>
      {/* Range control */}
      <input
        type="range"
        min="0" max="100" value={pct}
        onChange={(e) => setPct(Number(e.target.value))}
        aria-label="Vergelijk voor en na"
        data-testid="photo-preview-slider"
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
      />
    </div>
  );
}
